import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Permissions } from '@guard/Permissions';
import {
  OrgChartImportResponse,
  OrgChartImportStatuses,
} from '@interfaces/economic-groups/responses/org-chart-import.response';
import { RealtimeEventTypes } from '@interfaces/realtime/events';
import { ApplyOrgChartImportRequest } from '@interfaces/economic-groups/requests/apply-org-chart-import.request';
import { EconomicGroupTreeResponse } from '@interfaces/economic-groups/responses/economic-group.response';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { EconomicGroupOrgChartImportsHttpService } from '@services/http/economic-groups/economic-group-org-chart-imports-http.service';
import { RealtimeService } from '@services/realtime/realtime.service';
import { Observable, Subject, catchError, filter, finalize, map, of, take, tap } from 'rxjs';
import {
  buildOrgChartImportSteps,
  isOrgChartImportOpen,
  isOrgChartImportRunning,
} from './org-chart-import-steps.util';
import { describeOrgChartImportError } from './org-chart-import-review.util';

/** Maior arquivo aceito pelo backend (`IEconomicGroupOrgChartImportValidator.MaxFileBytes`). */
export const ORG_CHART_IMPORT_MAX_BYTES = 20 * 1024 * 1024;

/** Formatos aceitos (os mesmos do validador do backend). */
export const ORG_CHART_IMPORT_FORMATS = ['.pdf', '.png', '.jpg', '.jpeg'];

/**
 * Estado da importação de organograma no builder do Grupo Econômico (card 3578). Provido pelo
 * `EconomicGroupBuilderComponent`; a modal de envio e o cartão de progresso injetam a mesma instância.
 *
 * - `init(groupId)`: busca a importação aberta (`GET /latest`) para retomar quem saiu e voltou, e passa a ouvir
 *   `economic-group.org-chart-import-changed` (tópico `tenant`, automático), filtrando pelo grupo. Evento é dica: a
 *   tela refaz o `GET /{importId}`. Na reconexão do hub relê o `/latest`. Sem polling.
 * - Uma importação aberta por grupo (regra do backend): evento de outra importação do mesmo grupo troca a atual.
 * - `Applied` vinda de fora (outra aba, MCP) emite `treeChanged$` para o builder reler a árvore.
 */
@Injectable()
export class OrgChartImportStateService {
  private readonly http = inject(EconomicGroupOrgChartImportsHttpService);
  private readonly realtime = inject(RealtimeService);
  private readonly permissions = inject(PermissionsService);
  private readonly toast = inject(ToastHelperService);
  private readonly destroyRef = inject(DestroyRef);

  readonly canImport = toSignal(
    this.permissions.hasPermission(Permissions.EconomicGroupOrgChartImport.IMPORT),
    { initialValue: false }
  );
  readonly canApply = toSignal(
    this.permissions.hasPermission(Permissions.EconomicGroupOrgChartImport.APPLY),
    { initialValue: false }
  );
  readonly canDiscard = toSignal(
    this.permissions.hasPermission(Permissions.EconomicGroupOrgChartImport.DISCARD),
    { initialValue: false }
  );

  /** Importação exibida no cartão (lendo, pronta ou falhou); `null` quando não há. */
  readonly current = signal<OrgChartImportResponse | null>(null);
  readonly isStarting = signal(false);
  readonly isDiscarding = signal(false);
  readonly isApplying = signal(false);

  readonly isOpen = computed(() => isOrgChartImportOpen(this.current()));
  readonly isRunning = computed(() => isOrgChartImportRunning(this.current()));
  readonly isReady = computed(() => this.current()?.status === OrgChartImportStatuses.Ready);
  readonly isFailed = computed(() => this.current()?.status === OrgChartImportStatuses.Failed);
  readonly steps = computed(() => buildOrgChartImportSteps(this.current()));

  /** A árvore do grupo mudou por uma importação aplicada fora desta tela (outra aba, MCP). */
  readonly treeChanged$ = new Subject<void>();

  /** A pessoa clicou em "Revisar importação" com a proposta pronta; a modal de revisão assina este Subject. */
  readonly reviewRequested$ = new Subject<OrgChartImportResponse>();

  private groupId: string | null = null;
  /** Última importação aplicada por esta tela: o evento `Applied` dela não relê a árvore de novo. */
  private lastAppliedId: string | null = null;

  init(groupId: string): void {
    if (this.groupId) return;
    this.groupId = groupId;

    // A permissão pode chegar depois do primeiro render; o `/latest` só roda com ela (o endpoint exige IMPORT).
    this.permissions
      .hasPermission(Permissions.EconomicGroupOrgChartImport.IMPORT)
      .pipe(filter(Boolean), take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadLatest());

    this.realtime
      .on(RealtimeEventTypes.EconomicGroupOrgChartImportChanged)
      .pipe(
        filter(
          envelope =>
            envelope.payload?.economicGroupId?.toLowerCase() === this.groupId?.toLowerCase()
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(envelope => this.refresh(envelope.payload.importId));

    this.realtime.reconnected$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadLatest());
  }

  /** Envia o arquivo. A importação nova substitui a aberta (o backend descarta a anterior). */
  start(file: File): Observable<OrgChartImportResponse | null> {
    if (!this.groupId) return of(null);
    this.isStarting.set(true);
    return this.http.start(this.groupId, { file }).pipe(
      tap(created => {
        if (created) this.current.set(created);
      }),
      finalize(() => this.isStarting.set(false))
    );
  }

  /** "Cancelar importação" (lendo) e "Fechar" (falhou): descarta no servidor e some com o cartão. */
  discard(): void {
    this.discardCurrent().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  /**
   * Descarta a importação atual e diz se deu certo. Sem a permissão de descartar, só esconde nesta tela (a importação
   * continua aberta no servidor). Erro vira toast com a mensagem do backend.
   */
  discardCurrent(): Observable<boolean> {
    const item = this.current();
    if (!item || !this.groupId || this.isDiscarding()) return of(false);

    if (!this.canDiscard()) {
      this.current.set(null);
      return of(true);
    }

    this.isDiscarding.set(true);
    return this.http.discard(this.groupId, item.id).pipe(
      map(() => {
        this.current.set(null);
        return true;
      }),
      catchError(error => {
        this.toast.showToastError(
          'Não deu para descartar a importação',
          describeOrgChartImportError(error) ??
            'Nada da importação foi gravado no grupo. Tente de novo em instantes.'
        );
        return of(false);
      }),
      finalize(() => this.isDiscarding.set(false))
    );
  }

  /**
   * Aplica a proposta revisada. No sucesso limpa a importação e emite `treeChanged$` (o builder relê a árvore em
   * silêncio). O erro segue para quem chamou, que mostra a mensagem do backend.
   */
  apply(payload: ApplyOrgChartImportRequest): Observable<EconomicGroupTreeResponse | null> {
    const item = this.current();
    if (!item || !this.groupId) return of(null);

    this.isApplying.set(true);
    return this.http.apply(this.groupId, item.id, payload).pipe(
      tap(() => {
        this.lastAppliedId = item.id;
        this.current.set(null);
        this.treeChanged$.next();
      }),
      finalize(() => this.isApplying.set(false))
    );
  }

  /** "Revisar importação": pede a abertura da modal de revisão com a proposta pronta. */
  openReview(): void {
    const item = this.current();
    if (!item || item.status !== OrgChartImportStatuses.Ready) return;
    this.reviewRequested$.next(item);
  }

  private loadLatest(): void {
    if (!this.groupId) return;
    this.http
      .getLatest(this.groupId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: item => this.current.set(isOrgChartImportOpen(item) ? item : null),
        // Falhar ao retomar não trava o builder; o próximo evento ou a reconexão tenta de novo.
        error: () => undefined,
      });
  }

  /** Relê a importação do evento. Sem IMPORT o GET daria 403, então quem não pode importar não relê. */
  private refresh(importId: string): void {
    if (!this.groupId || !importId || !this.canImport()) return;
    this.http
      .getById(this.groupId, importId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: item => this.applyRefreshed(item),
        error: () => undefined,
      });
  }

  private applyRefreshed(item: OrgChartImportResponse | null): void {
    if (!item) return;
    const current = this.current();

    if (item.status === OrgChartImportStatuses.Applied) {
      if (!current || current.id === item.id) this.current.set(null);
      if (item.id !== this.lastAppliedId) this.treeChanged$.next();
      return;
    }

    if (item.status === OrgChartImportStatuses.Discarded) {
      if (current?.id === item.id) this.current.set(null);
      return;
    }

    // Lendo, pronta ou falhou: é a importação aberta do grupo (uma por vez), mesmo que tenha começado em outra aba.
    this.current.set(item);
  }
}
